/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable array-callback-return */
import React, { useEffect, useMemo, useState } from "react";

import { Grid, MenuItem } from "@mui/material";
import { AddCircleOutline as AddCircleOutlineIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { differenceInDays } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc, format } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { Input, Select } from "../../../../../../components/Form";
import Button, { Color } from "../../../../../../components/Form/Button";
import Checkbox from "../../../../../../components/Form/Checkbox";
import FieldView from "../../../../../../components/Form/FieldView";
import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import LoadingModal from "../../../../../../components/LoadingModal";
import { ExternalList } from "../../../../../../context/MovingResources";
import { useAppState } from "../../../../../../context/StateContext";
import { TaskDate } from "../../../../../../models/sales";
import { Task } from "../../../../../../models/task";
import { TaskType as ITaskType } from "../../../../../../models/task.type";
import TaskService from "../../../../../../services/task.service";
import { Status, TaskType } from "../../../../../../util/enums";
import groupBy from "../../../../../../util/groupBy";
import useStyles from "./styles";
import classNames from "classnames";

const UNIQUE_TASKS = [TaskType.PREFERRED_UPLIFT];

type Props = {
  taskDates?: TaskDate[];
  dataCyCancel?: string;
  dataCySave?: string;
  tasks?: Task[];
  isUpdating?: boolean;
  externalList?: ExternalList;
  isOnViewMode?: boolean;
  isSavingAndSending?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
  handleUpdate?: (taskDate?: TaskDate[], type?: string, tasks?: Task[]) => void;
  onSaveAndSend?: () => void;
};

const Tasks = (props: Props) => {
  const {
    taskDates,
    dataCyCancel,
    dataCySave,
    tasks,
    isUpdating,
    externalList,
    isOnViewMode = false,
    isSavingAndSending = false,
    handleUpdate,
    onCancel,
    onSave,
    onSaveAndSend,
  } = props;

  const { currentUser } = useAppState();

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const [taskTypeSelected, setTaskTypeSelected] = useState<ITaskType>();
  const [date, setDate] = useState<string>();

  const [taskTypes, setTaskTypes] = useState<ITaskType[]>(
    externalList?.taskTypeList?.filter((taskType) => taskType.isBookable) || []
  );

  const [tasksDatesGrouped, setTasksDatesGrouped] = useState<{
    [x: string]: Array<TaskDate>;
  }>();

  const services = useMemo(() => [TaskType.HOME_SURVEY, TaskType.VIDEO_SURVEY], []);

  useQuery(
    ["task.types"],
    () =>
      TaskService.types({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify(["sequence"]),
        isBookable: 1,
      }),
    {
      enabled: !externalList?.taskTypeList,
      onSuccess: (response) => {
        if (response && response.data) setTaskTypes(response.data);
      },
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    setTasksDatesGrouped(groupBy(taskDates || [], "taskType.name"));
  }, [taskDates]);

  const validateForm = () => {
    if (!taskTypeSelected) {
      NotificationToast.warning(translate("selectATask"));

      return false;
    }

    if (!date) {
      NotificationToast.warning(translate("enterADateInField"));

      return false;
    }

    if (differenceInDays(new Date(date), new Date()) < 0) {
      NotificationToast.warning(translate("theSurveyDateMustBeGreaterThanTheCurrentDate"));

      return false;
    }
    setDate(undefined);
    setTaskTypeSelected(undefined);
    NotificationToast.success(translate("taskAdded"));

    return true;
  };

  const onAddTask = () => {
    if (validateForm()) {
      const taskDate = {
        date,
        taskType: taskTypeSelected,
        taskTypeId: taskTypeSelected?.id,
      } as TaskDate;

      const formattedDateWithCurrentTime = utcToZonedTime(
        new Date(String(date)).setHours(new Date().getHours(), new Date().getMinutes()),
        Intl.DateTimeFormat().resolvedOptions().timeZone
      ).toISOString();

      if (handleUpdate) {
        if (taskDate?.taskType?.name === TaskType.HOME_SURVEY) {
          const newTask = {
            type: TaskType.HOME_SURVEY,
            startDate: String(formattedDateWithCurrentTime),
          } as Task;

          const newTasks = [...(tasks || []), newTask];

          handleUpdate(undefined, undefined, newTasks);

          return;
        }

        if (taskDate?.taskType?.name === TaskType.VIDEO_SURVEY) {
          const newTask = {
            type: TaskType.VIDEO_SURVEY,
            startDate: String(formattedDateWithCurrentTime),
          } as Task;

          const newTasks = [...(tasks || []), newTask];

          handleUpdate(undefined, undefined, newTasks);

          return;
        }
      }

      if (
        UNIQUE_TASKS.includes(taskTypeSelected?.name as TaskType) &&
        taskTypeSelected &&
        tasksDatesGrouped &&
        tasksDatesGrouped[taskTypeSelected.name]
      ) {
        onChangeTaskDate(taskTypeSelected.name, taskDate, 0);

        return;
      }

      const tasksDatesUpdated: TaskDate[] = [...(taskDates || [])];

      tasksDatesUpdated.push(taskDate);

      if (handleUpdate) handleUpdate(tasksDatesUpdated);
    }
  };

  const onRemove = (key: string, index?: number) => {
    if (tasksDatesGrouped && index !== undefined) {
      const tasksDatesUpdated = tasksDatesGrouped[key];

      if (tasksDatesUpdated && tasksDatesUpdated?.length > 0) {
        delete tasksDatesUpdated[index];

        const tasksDatesGroupedUpdated = {
          ...tasksDatesGrouped,
          [key]: tasksDatesUpdated.filter(Boolean),
        };

        const tasksDates: TaskDate[] = [];
        const keys = Object.getOwnPropertyNames(tasksDatesGroupedUpdated);

        keys.forEach((key) => {
          if (tasksDatesGroupedUpdated[key]) tasksDates.push(...tasksDatesGroupedUpdated[key]);
        });
        if (handleUpdate) handleUpdate(tasksDates);
      }
    }
  };

  const onChangeTaskDate = (key: string, taskDate: TaskDate, index?: number) => {
    if (services.some((service) => service === key)) {
      if (index !== undefined) {
        const tasksUpdated = [...(tasks || [])];

        if (tasksUpdated[index]) {
          tasksUpdated[index] = {
            ...tasksUpdated[index],
            ...taskDate,
            startDate: taskDate?.date || tasksUpdated[index]?.startDate,
          };
        }

        if (handleUpdate) handleUpdate(undefined, undefined, tasksUpdated);

        return;
      }
    }

    if (tasksDatesGrouped && index !== undefined) {
      const tasksDatesUpdated = tasksDatesGrouped[key];

      if (tasksDatesUpdated && tasksDatesUpdated?.length > 0) {
        tasksDatesUpdated[index] = {
          ...tasksDatesUpdated[index],
          ...taskDate,
        };

        const tasksDatesGroupedUpdated = {
          ...tasksDatesGrouped,
          [key]: tasksDatesUpdated,
        };

        const tasksDates: TaskDate[] = [];
        const keys = Object.getOwnPropertyNames(tasksDatesGroupedUpdated);

        keys.forEach((key) => {
          if (tasksDatesGroupedUpdated[key]) tasksDates.push(...tasksDatesGroupedUpdated[key]);
        });
        if (handleUpdate) handleUpdate(tasksDates);
      }
    }
  };

  const renderTasks = () => {
    if (taskTypes && taskTypes?.length > 0) {
      return taskTypes?.map((typeTask) => {
        return (
          <MenuItem key={typeTask.name} value={typeTask?.id}>
            {typeTask.name}
          </MenuItem>
        );
      });
    }

    return null;
  };

  const renderTimeByType = (
    taskDate: TaskDate,
    type: TaskType,
    index?: number,
    disabled?: boolean
  ) => {
    const allowed = [TaskType.HOME_SURVEY, TaskType.VIDEO_SURVEY];

    if (allowed.includes(type)) {
      return (
        <Grid item xs={12} sm={6}>
          {isOnViewMode ? (
            <FieldView
              label={translate("time") ?? ""}
              value={
                taskDate?.date &&
                format(
                  utcToZonedTime(taskDate?.date, Intl.DateTimeFormat().resolvedOptions().timeZone),
                  "HH:mm"
                )
              }
            />
          ) : (
            <Input
              disabled={disabled}
              name="time"
              label={translate("time")}
              type="time"
              data-cy={index !== undefined ? `input-time-${index}` : "main-input-time"}
              onChange={(event) => {
                if (event?.target?.value) {
                  const timeSplitted = event.target.value.split(":");

                  if (timeSplitted && timeSplitted?.length > 0 && taskDate?.date) {
                    const oldDate = new Date(taskDate?.date);

                    oldDate.setHours(Number(timeSplitted[0]), Number(timeSplitted[1]));

                    const date = zonedTimeToUtc(
                      oldDate,
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    ).toISOString();

                    if (date) {
                      if (new Date(date).getTime() < new Date().getTime()) {
                        NotificationToast.warning(
                          translate("theSurveyDateMustBeGreaterThanTheCurrentDate")
                        );

                        return;
                      }

                      onChangeTaskDate(
                        type,
                        {
                          date,
                        },
                        index
                      );
                    }
                  }
                }
              }}
              value={
                taskDate?.date
                  ? format(
                      utcToZonedTime(
                        taskDate?.date,
                        Intl.DateTimeFormat().resolvedOptions().timeZone
                      ),
                      "HH:mm"
                    )
                  : ""
              }
            />
          )}
        </Grid>
      );
    }

    return null;
  };

  const renderDeleteIconByType = (type: TaskType, index?: number) => {
    const notAllowed = [TaskType.HOME_SURVEY, TaskType.VIDEO_SURVEY, TaskType.PREFERRED_UPLIFT];

    if (!notAllowed.includes(type)) {
      return (
        <Grid item container justifyContent="flex-end" xs={2}>
          <DeleteIcon
            onClick={() => onRemove(type, index)}
            className={classes.icon}
            htmlColor="#CF4842"
            data-cy={index !== undefined ? `icon-delete-${index}` : "main-icon-delete"}
          />
        </Grid>
      );
    }
  };

  const renderResponsiveDate = (
    taskDate: TaskDate,
    type: TaskType,
    index?: number,
    disabled?: boolean
  ) => {
    const showTime = [TaskType.HOME_SURVEY, TaskType.VIDEO_SURVEY];

    return (
      <Grid item xs={12} sm={showTime?.includes(type) ? 6 : undefined}>
        {isOnViewMode ? (
          <FieldView
            label={translate("date") ?? ""}
            value={
              taskDate?.date &&
              format(
                utcToZonedTime(taskDate?.date, Intl.DateTimeFormat().resolvedOptions().timeZone),
                "dd/MM/yyyy"
              )
            }
          />
        ) : (
          <Input
            disabled={disabled}
            name="date"
            label={translate("date")}
            type="date"
            min={showTime?.includes(type) ? format(new Date(), "yyyy-MM-dd") : undefined}
            disablePast={showTime?.includes(type) ? true : undefined}
            data-cy={index !== undefined ? `input-date-${index}` : "main-input-date"}
            onChange={(event) => {
              if (event?.target?.value) {
                const dateSplitted = event.target.value.split("-");

                if (dateSplitted && dateSplitted?.length > 0 && taskDate?.date) {
                  const oldDate = new Date(taskDate?.date);

                  oldDate.setFullYear(
                    Number(dateSplitted[0]),
                    Number(dateSplitted[1]) - 1,
                    Number(dateSplitted[2])
                  );

                  const date = zonedTimeToUtc(
                    oldDate,
                    Intl.DateTimeFormat().resolvedOptions().timeZone
                  ).toISOString();

                  if (date) {
                    if (new Date(date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) {
                      NotificationToast.warning(
                        translate("theSurveyDateMustBeGreaterThanTheCurrentDate")
                      );

                      return;
                    }

                    onChangeTaskDate(type, { date }, index);
                  }
                }
              }
            }}
            value={
              taskDate?.date
                ? format(
                    utcToZonedTime(
                      taskDate?.date,
                      Intl.DateTimeFormat().resolvedOptions().timeZone
                    ),
                    "yyyy-MM-dd"
                  )
                : ""
            }
          />
        )}
      </Grid>
    );
  };

  const renderSendEmailOrSMSInvite = (taskDate: TaskDate, type: TaskType, index?: number) => {
    const showInvite = [TaskType.VIDEO_SURVEY];

    const getValueCheckbox = (typeValue: "sendToEmail" | "sendToSms") => {
      let value = false;

      if (tasks && tasks.length > 0 && index !== undefined) {
        const taskFound = tasks[index];

        if (taskFound) {
          value = taskFound[typeValue];
        }
      }

      return value;
    };

    if (showInvite.includes(type)) {
      return (
        <Grid item xs={12} sm={6}>
          <section className={classes.sectionWithoutDivisor}>
            <div className={classes.flexColumn}>
              <span
                className={`${classes.textInvitation} ${taskDate?.id ? classes.textDisabled : ""}`}
              >
                {translate("automaticallyInvitationSentBy")}
              </span>

              <div className={classNames(classes.flexRow, classes.gap16px)}>
                <Checkbox
                  label={translate("email")}
                  checked={getValueCheckbox("sendToEmail")}
                  disabled={!!taskDate?.id}
                  onChange={(event, checked) => {
                    onChangeTaskDate(type, { sendToEmail: checked } as TaskDate, index);
                  }}
                />

                <Checkbox
                  label={translate("sms")}
                  disabled={!!taskDate?.id}
                  checked={getValueCheckbox("sendToSms")}
                  onChange={(event, checked) => {
                    onChangeTaskDate(type, { sendToSms: checked } as TaskDate, index);
                  }}
                />
              </div>
            </div>
          </section>
        </Grid>
      );
    }
  };

  const renderRowTaskDate = (
    taskDate: TaskDate,
    taskType: ITaskType,
    showIndex: boolean,
    taskInvite?: boolean,
    isMultiDates?: boolean,
    index?: number,
    disabled?: boolean,
    renderIndex?: () => string
  ) => {
    return (
      <>
        <Grid
          container
          direction="row"
          alignItems="center"
          className={isMultiDates ? classes.rowGridMulti : classes.rowGrid}
          spacing={2}
          key={index}
        >
          <Grid item xs={10}>
            <span data-cy={taskType?.name} className={classes.rowTitle}>
              {taskType?.name} {/* eslint-disable-next-line no-nested-ternary */}
              {showIndex && renderIndex
                ? renderIndex()
                : showIndex && index !== undefined
                ? `(${index + 1})`
                : ""}
            </span>
          </Grid>
          {!isOnViewMode && renderDeleteIconByType(taskType.name as TaskType, index)}
          {renderResponsiveDate(taskDate, taskType.name as TaskType, index, disabled)}
          {renderTimeByType(taskDate, taskType.name as TaskType, index, disabled)}
          {!isOnViewMode && renderSendEmailOrSMSInvite(taskDate, taskType.name as TaskType, index)}
        </Grid>
        {isOnViewMode && <hr className="mt-1" />}
      </>
    );
  };

  const renderMainTasks = (typesAllowed?: TaskType[]) => {
    if (tasks && tasks.length > 0) {
      let indexDisplayed = 0;

      // eslint-disable-next-line array-callback-return
      return tasks.map((task, index) => {
        if (typesAllowed && task?.type && typesAllowed?.includes(task?.type as TaskType)) {
          indexDisplayed += 1;

          return renderRowTaskDate(
            { date: task.startDate, id: task?.id },
            { name: task.type },
            true,
            !!(task.sendToEmail && task.sendToSms),
            false,
            index,
            task?.id
              ? new Date().setHours(0, 0, 0, 0) > new Date(task.startDate).setHours(0, 0, 0, 0)
              : false,
            () => `(${indexDisplayed})`
          );
        }
      });
    }
  };

  const renderTaskDates = (
    typesAllowed?: TaskType[],
    typesNotAllowed?: TaskType[],
    isMultiDates?: boolean
  ) => {
    if (tasksDatesGrouped) {
      return taskTypes
        ?.filter((taskType) => {
          if (typesAllowed && typesNotAllowed)
            return (
              typesAllowed?.includes(taskType.name as TaskType) &&
              !typesNotAllowed?.includes(taskType.name as TaskType)
            );
          if (typesAllowed) return typesAllowed?.includes(taskType.name as TaskType);
          if (typesNotAllowed) return !typesNotAllowed?.includes(taskType.name as TaskType);
        })
        .sort((taskTypeA, taskTypeB) =>
          typesAllowed
            ? typesAllowed?.indexOf(taskTypeA.name as TaskType) -
              typesAllowed?.indexOf(taskTypeB.name as TaskType)
            : 0
        )
        .map((taskType) => {
          if (tasksDatesGrouped[taskType.name]) {
            const showIndex = tasksDatesGrouped[taskType.name].length > 1;
            const showInvite = taskType.name === TaskType.VIDEO_SURVEY;

            return (
              <div
                key={taskType?.name}
                className={isMultiDates ? classes.containerTaskDates : undefined}
              >
                {tasksDatesGrouped[taskType.name]?.map((taskDate, index) => {
                  return renderRowTaskDate(
                    taskDate,
                    taskType,
                    showIndex,
                    showInvite,
                    isMultiDates,
                    index
                  );
                })}
              </div>
            );
          }
        });
    }
  };

  const renderDateHeader = () => {
    const allowedDates: string[] = [TaskType.HOME_SURVEY, TaskType.VIDEO_SURVEY];

    return (
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="flex-end"
        className={classes.grid}
        spacing={2}
      >
        <Grid item xs={12} sm={4}>
          <Select
            defaultValue=""
            name="task"
            label={translate("task")}
            value={taskTypeSelected?.id || ""}
            onChange={(event) => {
              const find = taskTypes?.find(
                (taskType) => taskType?.id === Number(event.target.value)
              );

              setTaskTypeSelected(find);
            }}
            data-cy="select-task"
            placeholder={translate("searchTask")}
            displayEmpty
          >
            {renderTasks()}
          </Select>
        </Grid>
        <Grid item xs={12} sm={7}>
          <Input
            name="date"
            label={translate("date")}
            type="date"
            min={
              taskTypeSelected &&
              taskTypeSelected.name &&
              allowedDates?.includes(taskTypeSelected.name)
                ? format(new Date(), "yyyy-MM-dd")
                : undefined
            }
            disablePast={
              !!(
                taskTypeSelected &&
                taskTypeSelected.name &&
                allowedDates?.includes(taskTypeSelected.name)
              )
            }
            value={
              date
                ? format(
                    utcToZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone),
                    "yyyy-MM-dd"
                  )
                : ""
            }
            onChange={(event) => {
              setDate(
                zonedTimeToUtc(
                  event.target.value,
                  Intl.DateTimeFormat().resolvedOptions().timeZone
                ).toISOString()
              );
            }}
            data-cy="input-date"
          />
        </Grid>
        <Grid item xs={12} sm={1}>
          <Button
            data-cy="button-add"
            onClick={onAddTask}
            startIcon={<AddCircleOutlineIcon fontSize="small" className={classes.iconButton} />}
            customColor={Color.SECONDARY}
          >
            {translate("add")}
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      <div className={isOnViewMode ? classes.containerView : classes.container}>
        {!isOnViewMode && (
          <>
            <span className={classes.title}>{translate("addATask")}</span>
            {renderDateHeader()}
            <div className={classes.line} />
          </>
        )}
        {renderMainTasks([TaskType.HOME_SURVEY])}
        {renderMainTasks([TaskType.VIDEO_SURVEY])}
        {renderTaskDates([TaskType.PREFERRED_UPLIFT])}
        {renderTaskDates(
          undefined,
          [TaskType.VIDEO_SURVEY, TaskType.HOME_SURVEY, TaskType.PREFERRED_UPLIFT],
          true
        )}
        {!isOnViewMode && (
          <div className={classes.buttons}>
            <ActionButtons
              showSaveAndSendButton={isSavingAndSending}
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              dataCyCancel={dataCyCancel}
              dataCySave={dataCySave}
              onCancel={onCancel}
              onSave={onSave}
              onSaveAndSend={onSaveAndSend}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default Tasks;
